import { Button } from '@startuptools/ui/Button';
import { Chip } from '@startuptools/ui/Chip';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { Divider } from '@startuptools/ui/Divider';
import { List } from '@startuptools/ui/List';
import { ListItem } from '@startuptools/ui/ListItem';
import { ListItemContent } from '@startuptools/ui/ListItemContent';
import { Stack } from '@startuptools/ui/Stack';
import { StripeCurrencyFormat } from '@startuptools/ui/StripeCurrencyFormat';
import { Typography } from '@startuptools/ui/Typography';
import { GqlStripeInvoiceStatus, type GqlStripeInvoicesQuery } from '../../graphql/react-operations';
import { Trans, useLingui } from '@lingui/react';
import { DateTime } from 'luxon';
import { Fragment } from 'react';

const InvoiceStatusChip = ({ children }: { children: GqlStripeInvoiceStatus | null | undefined }) => {
  if (!children) {
    return;
  }
  switch (children) {
    case GqlStripeInvoiceStatus.Open: {
      return (
        <Chip size="sm" variant="outlined" color="primary">
          <Trans id="Open" />
        </Chip>
      );
    }
    case GqlStripeInvoiceStatus.Paid: {
      return (
        <Chip size="sm" variant="outlined" color="success">
          <Trans id="Paid" />
        </Chip>
      );
    }
    case GqlStripeInvoiceStatus.Void: {
      return (
        <Chip size="sm" variant="outlined" color="neutral">
          <Trans id="Void" />
        </Chip>
      );
    }
    case GqlStripeInvoiceStatus.Draft: {
      return (
        <Chip size="sm" variant="outlined" color="neutral">
          <Trans id="Draft" />
        </Chip>
      );
    }
    case GqlStripeInvoiceStatus.Uncollectible: {
      return (
        <Chip size="sm" variant="outlined" color="warning">
          <Trans id="Uncollectible" />
        </Chip>
      );
    }
  }
};

export const Invoices = ({ invoices }: { invoices: GqlStripeInvoicesQuery['stripeInvoices'] }) => {
  const { i18n } = useLingui();

  return (
    <Stack gap={1}>
      <Typography level="h4">
        <Trans id="Invoices" />
      </Typography>
      <List>
        {invoices.map(i => (
          <Fragment key={i.id}>
            <ListItem
              endAction={
                <Stack direction="row" gap={1}>
                  <Button
                    component="a"
                    variant="outlined"
                    size="sm"
                    color={i.paid ? 'neutral' : 'primary'}
                    href={i.hosted_invoice_url || undefined}
                    target="_blank"
                  >
                    {i.paid ? <Trans id="Receipt" /> : <Trans id="Show more" />}
                  </Button>
                </Stack>
              }
            >
              <ListItemContent>
                <Typography level="title-sm">
                  {i.number}{' '}
                  {i.paid ? (
                    <Chip variant="outlined" color="success" size="sm">
                      <Trans
                        id="Paid <date></date>"
                        components={{
                          date: (
                            <DateFormat locale={i18n.locale}>
                              {DateTime.fromSeconds(i.status_transitions.paid_at || 0)}
                            </DateFormat>
                          ),
                        }}
                      />
                    </Chip>
                  ) : (
                    <>
                      <InvoiceStatusChip>{i.status}</InvoiceStatusChip>
                      {i.auto_advance && (
                        <Chip>
                          <Trans id="Automatic collection" />
                        </Chip>
                      )}
                    </>
                  )}
                </Typography>
                <Typography level="body-sm">
                  <Trans id="Amount:" />
                  <StripeCurrencyFormat locale={i18n.locale} currency={i.currency} minimumFractionDigits={2}>
                    {i.total}
                  </StripeCurrencyFormat>
                </Typography>
              </ListItemContent>
            </ListItem>
            <Divider />
          </Fragment>
        ))}
      </List>
    </Stack>
  );
};
